import { Autocomplete, type Item } from '@ads/components-react';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/shared/ui/organisms/PageHeader';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';

import { useTutorStudentSearch } from '../../../hooks/useTutorStudentSearch';
import { SurfaceCard } from '../../molecules/SurfaceCard';

export interface TutorStudentSearchTemplateProps {
  readonly onSelectStudent: (personId: number) => void;
}

/**
 * Template da entrada da tela de aluno: busca por nome e navegação para o relatório.
 *
 * Um autocomplete, não uma tabela: o operador chega aqui sabendo de quem quer ver o
 * consumo, e uma listagem de pessoas duplicaria a tela que já existe em `/persons`.
 *
 * A busca depende do controller `person` — um operador sem acesso a Pessoas não consegue
 * chegar ao relatório por aqui.
 */
export function TutorStudentSearchTemplate({ onSelectStudent }: TutorStudentSearchTemplateProps) {
  const { t } = useTranslation('tutorAI');
  const { t: tCommon } = useTranslation('common');
  const [search, setSearch] = useState('');
  const { items, isQuerySuccess } = useTutorStudentSearch(search);

  const notFoundMessage = getAutocompleteSearchMessage(
    search.length,
    isQuerySuccess,
    items.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  return (
    <div className="flex flex-col gap-xs">
      <PageHeader title={t('student.search.title')} subtitle={t('student.search.subtitle')} />

      <SurfaceCard title={t('student.search.sectionTitle')}>
        <div className="p-xxs">
          <Autocomplete<string>
            label={t('student.search.label')}
            placeholder={t('student.search.placeholder')}
            size="md"
            list={items as Item<string>[]}
            custom={{ optionList: 'z-[100]', option: '!h-auto py-nano', input: '!w-full' }}
            {...(notFoundMessage !== undefined ? { notFoundMessage } : {})}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
            handleChange={(item: Item<string> | null) => {
              if (!item?.value) {
                setSearch('');
                return;
              }
              onSelectStudent(Number(item.value));
            }}
          />
        </div>
      </SurfaceCard>
    </div>
  );
}
